import { useMemo, useState } from 'react'
import { Layout } from '../components/Layout'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import { removeToken } from '../services/api'
import {
  Bell,
  CheckCircle2,
  Loader2,
  Lock,
  LogOut,
  Monitor,
  Moon,
  Save,
  Sun,
} from 'lucide-react'

const SETTINGS_KEY = 'zt_dashboard_settings'

function loadStoredSettings(): { pollInterval: number; wsEnabled: boolean } {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY)
    if (raw) {
      const parsed = JSON.parse(raw)
      return {
        pollInterval: Number.isFinite(Number(parsed.pollInterval)) ? Number(parsed.pollInterval) : 10,
        wsEnabled: typeof parsed.wsEnabled === 'boolean' ? parsed.wsEnabled : true,
      }
    }
  } catch {
    // ignore malformed settings
  }
  return { pollInterval: 10, wsEnabled: true }
}

export function SettingsPage() {
  const { theme, resolvedTheme, setTheme } = useTheme()
  const { logout } = useAuth()
  const [saved, setSaved] = useState(false)
  const initialSettings = useMemo(loadStoredSettings, [])
  const [pollInterval, setPollInterval] = useState(initialSettings.pollInterval)
  const [wsEnabled, setWsEnabled] = useState(initialSettings.wsEnabled)
  const [clearing, setClearing] = useState(false)

  const handleSave = () => {
    try {
      localStorage.setItem(SETTINGS_KEY, JSON.stringify({
        theme,
        pollInterval,
        wsEnabled,
      }))
      setSaved(true)
      setTimeout(() => setSaved(false), 2000)
    } catch {
      // ignore
    }
  }

  const handleClearStorage = async () => {
    try {
      setClearing(true)
      removeToken()
      localStorage.removeItem('zt_dashboard_theme')
      localStorage.removeItem(SETTINGS_KEY)
      logout()
      window.location.href = '/login'
    } finally {
      setClearing(false)
    }
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-2xl font-bold">Settings</h1>
          <p className="text-text-muted text-sm">Customize your dashboard preferences</p>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
              {resolvedTheme === 'dark' ? (
                <Moon className="w-5 h-5 text-primary" />
              ) : (
                <Sun className="w-5 h-5 text-primary" />
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold">Appearance</h2>
              <p className="text-sm text-text-muted">Choose your preferred theme</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {(['light', 'dark', 'system'] as const).map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setTheme(t)}
                className={`p-4 rounded-lg border text-left transition-colors ${
                  theme === t
                    ? 'border-primary bg-primary-50'
                    : 'border-border hover:bg-surface-elevated'
                }`}
              >
                <div className="font-medium capitalize">{t}</div>
                <div className="text-xs text-text-muted mt-1">
                  {t === 'light' && 'Always light mode'}
                  {t === 'dark' && 'Always dark mode'}
                  {t === 'system' && 'Follow system preference'}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
              <Monitor className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Dashboard</h2>
              <p className="text-sm text-text-muted">Configure polling and real-time updates</p>
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <label htmlFor="pollInterval" className="block text-sm font-medium mb-2">
                Poll Interval (seconds)
              </label>
              <input
                id="pollInterval"
                type="range"
                min={5}
                max={60}
                step={5}
                value={pollInterval}
                onChange={e => setPollInterval(Number(e.target.value))}
                className="w-full"
              />
              <div className="text-sm text-text-muted mt-1">{pollInterval} seconds</div>
            </div>

            <div className="flex items-center justify-between p-4 rounded-lg bg-surface-elevated">
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-primary" />
                <div>
                  <div className="font-medium">WebSocket Updates</div>
                  <div className="text-sm text-text-muted">Receive live device status updates</div>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={wsEnabled}
                  onChange={e => setWsEnabled(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-border peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary" />
              </label>
            </div>
          </div>
        </div>

        <div className="card p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-danger/10 flex items-center justify-center">
              <Lock className="w-5 h-5 text-danger" />
            </div>
            <div>
              <h2 className="text-lg font-semibold">Session</h2>
              <p className="text-sm text-text-muted">Manage your local session data</p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleClearStorage}
            disabled={clearing}
            className="btn btn-danger bg-danger text-white hover:bg-danger/90 disabled:opacity-50"
          >
            {clearing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Clearing...
              </>
            ) : (
              <>
                <LogOut className="w-4 h-4" />
                Clear Local Data & Logout
              </>
            )}
          </button>
        </div>

        <div className="flex items-center gap-4">
          <button
            type="button"
            onClick={handleSave}
            className="btn btn-primary"
          >
            <Save className="w-4 h-4" />
            Save Preferences
          </button>

          {saved && (
            <div className="flex items-center gap-2 text-success text-sm">
              <CheckCircle2 className="w-4 h-4" />
              Saved successfully
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}

